import { useCallback } from "react"
import { useMutation } from "urql"

import type {
  GoodsReceiptPallet,
  GoodsReceiptUpdateSuccess,
  ProcessGoodsReceiptMutation,
  ProcessGoodsReceiptMutationVariables,
} from "@/graphql/codegen/graphql"
import { GoodsReceiptStatus, ProcessGoodsReceiptDocument } from "@/graphql/codegen/graphql"
import type { DestinationOverride } from "@/screens/WarehouseReceiving/components/GoodsReceiptPalletsTable"

interface FinalizeCheckInResult {
  success: boolean
  agrNumber?: string
  error?: string
}

interface UseFinalizeCheckInReturn {
  finalizeCheckIn: (
    goodsReceiptId: string,
    pallets: GoodsReceiptPallet[],
    destinationOverrides: Map<string, DestinationOverride>
  ) => Promise<FinalizeCheckInResult>
}

export const useFinalizeCheckIn = (): UseFinalizeCheckInReturn => {
  const [, executeMutation] = useMutation<ProcessGoodsReceiptMutation, ProcessGoodsReceiptMutationVariables>(
    ProcessGoodsReceiptDocument
  )

  const finalizeCheckIn = useCallback(
    async (
      goodsReceiptId: string,
      pallets: GoodsReceiptPallet[],
      destinationOverrides: Map<string, DestinationOverride>
    ): Promise<FinalizeCheckInResult> => {
      const palletDestinations = pallets.map((pallet) => {
        const override = destinationOverrides.get(pallet.goodsReceiptPalletId)

        return {
          palletId: pallet.goodsReceiptPalletId,
          destinationWarehouseShelvingId: override?.warehouseShelvingId ?? null,
        }
      })

      const result = await executeMutation({
        input: {
          goodsReceiptId,
          status: GoodsReceiptStatus.CheckedIn,
          checkedInData: {
            palletDestinations,
          },
        },
      })

      if (result.error) {
        return {
          success: false,
          error: result.error.message || "Failed to finalize check-in. Please try again.",
        }
      }

      const data = result.data?.goodsReceipt.processReceipt

      if (data?.__typename === "GoodsReceiptUpdateSuccess") {
        return {
          success: true,
          agrNumber: (data as GoodsReceiptUpdateSuccess).goodsReceipt.agrNumber,
        }
      }

      if (data?.__typename === "GoodsReceiptUpdateFailure") {
        return {
          success: false,
          error: data.error || "Failed to finalize check-in. Please try again.",
        }
      }

      return {
        success: false,
        error: "Unexpected response from server.",
      }
    },
    [executeMutation]
  )

  return { finalizeCheckIn }
}
